/* crm-deal-workspace.jsx — Enhanced deal tabs for Phase 3
   Provides: BuyingCommitteeTab, CompetitorsTab, DealProductsTab,
             DealChecklistTab, DealIntelligenceTab, DealReopenModal
   All components self-register on window. */
import { Modal } from '../../shared/ui/index.jsx';

const { useState, useEffect, useMemo, useRef, useCallback } = React;

/* ── helpers ── */
const fmtMoney = (n) => '$' + (n >= 1_000_000 ? (n / 1_000_000).toFixed(1) + 'M' : n >= 1_000 ? (n / 1_000).toFixed(0) + 'K' : Math.round(n || 0));
const apiAvailable = () => typeof window.__nx_api?.dealEnrichment !== 'undefined';
const daysBetween  = (a, b) => Math.floor(Math.abs(new Date(b) - new Date(a)) / 86_400_000);

/* ── Role colours for Buying Committee ── */
const ROLE_TONE = {
  PRIMARY_CONTACT: 'blue',
  DECISION_MAKER:  'purple',
  CHAMPION:        'green',
  ECONOMIC_BUYER:  'amber',
  INFLUENCER:      'gray',
  STAKEHOLDER:     'gray',
  BLOCKER:         'red',
};
const ROLE_LABELS = {
  PRIMARY_CONTACT: 'Primary Contact',
  DECISION_MAKER:  'Decision Maker',
  CHAMPION:        'Champion',
  ECONOMIC_BUYER:  'Economic Buyer',
  INFLUENCER:      'Influencer',
  STAKEHOLDER:     'Stakeholder',
  BLOCKER:         'Blocker',
};
const ALL_ROLES    = Object.keys(ROLE_LABELS);
const BLANK_MEMBER = { name: '', title: '', email: '', role: 'STAKEHOLDER', influence: 50, notes: '' };

/* ══════════════════════════════════════════════════════════════
   BUYING COMMITTEE TAB
   ══════════════════════════════════════════════════════════════ */
const BuyingCommitteeTab = ({ record }) => {
  const toast      = useToast();
  const store      = useStore();
  const wsId       = window.__nx_auth?.user?.activeWorkspaceId;
  const dealId     = record?.id;
  const canManage  = !!(window.__nx_rbac?.can('crm.pipeline.manage'));

  const [contacts,    setContacts]    = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState(null);
  const [formMode,    setFormMode]    = useState(null);   // null | 'add' | 'edit'
  const [editTarget,  setEditTarget]  = useState(null);
  const [draft,       setDraft]       = useState(BLANK_MEMBER);
  const [saving,      setSaving]      = useState(false);
  const [removing,    setRemoving]    = useState(null);

  useEffect(() => {
    if (!wsId || !dealId || dealId.includes('_')) { setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    setError(null);
    if (apiAvailable()) {
      window.__nx_api.dealEnrichment.listContacts(wsId, dealId)
        .then(r => { if (!cancelled) setContacts(Array.isArray(r) ? r : (r?.data ?? [])); })
        .catch(e => { if (!cancelled) setError(e?.message || 'Failed to load committee'); })
        .finally(() => { if (!cancelled) setLoading(false); });
    } else {
      setLoading(false);
    }
    return () => { cancelled = true; };
  }, [wsId, dealId]);

  const openAdd = () => {
    setDraft(BLANK_MEMBER);
    setEditTarget(null);
    setFormMode('add');
  };

  const openEdit = (c) => {
    setDraft({ name: c.name || '', title: c.title || '', email: c.email || '', role: c.role || 'STAKEHOLDER', influence: c.influence ?? 50, notes: c.notes || '' });
    setEditTarget(c);
    setFormMode('edit');
  };

  const closeForm = () => {
    if (saving) return;
    setFormMode(null);
    setEditTarget(null);
  };

  const handleSave = async () => {
    if (!draft.name.trim() || saving) return;
    setSaving(true);
    try {
      if (formMode === 'add') {
        const created = apiAvailable()
          ? await window.__nx_api.dealEnrichment.addContact(wsId, dealId, draft)
          : { id: 'local_' + Date.now(), ...draft };
        setContacts(c => [...c, created]);
        toast({ tone: 'success', icon: 'UserPlus', title: 'Contact added' });
      } else {
        const updated = apiAvailable()
          ? await window.__nx_api.dealEnrichment.updateContact(wsId, dealId, editTarget.id, draft)
          : { ...editTarget, ...draft };
        setContacts(c => c.map(x => x.id === editTarget.id ? updated : x));
        toast({ tone: 'success', icon: 'Check', title: 'Contact updated' });
      }
      closeForm();
    } catch (e) {
      toast({ tone: 'danger', icon: 'AlertCircle', title: e?.message || 'Failed to save contact' });
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = (c) => {
    store.confirm({
      title:       `Remove "${c.name}"?`,
      description: 'This committee member will be permanently removed from the deal.',
      danger:      true,
      onConfirm:   async () => {
        setRemoving(c.id);
        try {
          if (apiAvailable() && !c.id.startsWith('local_')) {
            await window.__nx_api.dealEnrichment.removeContact(wsId, dealId, c.id);
          }
          setContacts(all => all.filter(x => x.id !== c.id));
        } catch (e) {
          toast({ tone: 'danger', icon: 'AlertCircle', title: e?.message || 'Failed to remove contact' });
        } finally {
          setRemoving(null);
        }
      },
    });
  };

  const visibleCards = formMode === 'edit'
    ? contacts.filter(c => c.id !== editTarget?.id)
    : contacts;

  if (loading) return <div className="nx-drawer-page"><SkeletonRow /><SkeletonRow /></div>;

  return (
    <div className="nx-panel" style={{ padding: '16px 20px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <span style={{ fontWeight: 600, fontSize: 13 }}>
          Buying Committee{contacts.length > 0 && <span style={{ fontWeight: 400, color: 'var(--nx-text-muted)', marginLeft: 4 }}>({contacts.length})</span>}
        </span>
        {canManage && !formMode && (
          <button type="button" className="nx-btn nx-btn-sm nx-btn-outline" onClick={openAdd}>
            <NexusIcon name="Plus" size={12} /> Add Member
          </button>
        )}
      </div>

      {/* Inline add / edit form */}
      {formMode && (
        <div style={{ background: 'var(--nx-surface-alt)', borderRadius: 8, padding: 12, marginBottom: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <input
              className="nx-input"
              placeholder="Name *"
              value={draft.name}
              onChange={e => setDraft(d => ({ ...d, name: e.target.value }))}
              autoFocus
              disabled={saving}
              style={{ width: '100%' }}
            />
            <input
              className="nx-input"
              placeholder="Job title"
              value={draft.title}
              onChange={e => setDraft(d => ({ ...d, title: e.target.value }))}
              disabled={saving}
              style={{ width: '100%' }}
            />
          </div>
          <input
            className="nx-input"
            placeholder="Email"
            value={draft.email}
            onChange={e => setDraft(d => ({ ...d, email: e.target.value }))}
            disabled={saving}
            style={{ width: '100%' }}
          />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px', gap: 8, alignItems: 'center' }}>
            <select
              className="nx-input"
              value={draft.role}
              onChange={e => setDraft(d => ({ ...d, role: e.target.value }))}
              disabled={saving}
            >
              {ALL_ROLES.map(r => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
            </select>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <input
                type="range"
                min={0}
                max={100}
                value={draft.influence}
                onChange={e => setDraft(d => ({ ...d, influence: Number(e.target.value) }))}
                disabled={saving}
                style={{ flex: 1, minWidth: 0 }}
              />
              <span style={{ fontSize: 11, minWidth: 28, color: 'var(--nx-text-muted)', textAlign: 'right' }}>{draft.influence}%</span>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" className="nx-btn nx-btn-sm nx-btn-primary" onClick={handleSave} disabled={!draft.name.trim() || saving}>
              {saving ? '…' : formMode === 'add' ? 'Add Member' : 'Save Changes'}
            </button>
            <button type="button" className="nx-btn nx-btn-sm nx-btn-ghost" onClick={closeForm} disabled={saving}>Cancel</button>
          </div>
        </div>
      )}

      {error && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: 'rgba(220,38,38,0.08)', borderRadius: 6, marginBottom: 12 }}>
          <NexusIcon name="AlertCircle" size={14} style={{ color: 'var(--nx-danger)', flexShrink: 0 }} />
          <span style={{ fontSize: 12, color: 'var(--nx-danger)' }}>{error}</span>
        </div>
      )}

      {!error && contacts.length === 0 && !formMode && (
        <EmptyState
          icon="Users"
          title="No committee members"
          desc={canManage ? 'Add the key stakeholders involved in this deal.' : 'No stakeholders have been added to this deal yet.'}
          compact
          action={canManage && (
            <button type="button" className="nx-btn nx-btn-sm nx-btn-outline" onClick={openAdd}>
              <NexusIcon name="Plus" size={12} /> Add Member
            </button>
          )}
        />
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {visibleCards.map(c => (
          <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', background: 'var(--nx-surface-alt)', borderRadius: 8, border: '1px solid var(--nx-border)' }}>
            <Avatar name={c.name} size={32} tone="var(--color-primary-500)" style={{ flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</div>
              {c.title && <div style={{ fontSize: 11, color: 'var(--nx-text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.title}</div>}
              {c.email && !c.title && <div style={{ fontSize: 11, color: 'var(--nx-text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.email}</div>}
              <div style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                <Pill tone={ROLE_TONE[c.role] || 'gray'}>{ROLE_LABELS[c.role] || c.role}</Pill>
                {c.influence != null && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <div style={{ width: 50, height: 4, background: 'var(--nx-border)', borderRadius: 2 }}>
                      <div style={{ width: (c.influence || 0) + '%', height: '100%', background: 'var(--nx-accent)', borderRadius: 2 }} />
                    </div>
                    <span style={{ fontSize: 10, color: 'var(--nx-text-muted)' }}>{c.influence}%</span>
                  </div>
                )}
              </div>
            </div>
            {canManage && (
              <div style={{ display: 'flex', gap: 2, flexShrink: 0 }}>
                <button
                  type="button"
                  className="nx-icon-btn nx-icon-btn-ghost"
                  onClick={() => openEdit(c)}
                  disabled={!!formMode || removing === c.id}
                  title="Edit"
                >
                  <NexusIcon name="Pencil" size={12} />
                </button>
                <button
                  type="button"
                  className="nx-icon-btn nx-icon-btn-ghost"
                  onClick={() => handleRemove(c)}
                  disabled={!!formMode || removing === c.id}
                  title="Remove"
                >
                  <NexusIcon name={removing === c.id ? 'Loader2' : 'Trash2'} size={12} />
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
window.BuyingCommitteeTab = BuyingCommitteeTab;

/* ══════════════════════════════════════════════════════════════
   COMPETITORS TAB
   ══════════════════════════════════════════════════════════════ */
const STRENGTH_TONE    = { High: 'red', Medium: 'amber', Low: 'green', Unknown: 'gray' };
const STATUS_TONE      = { Active: 'blue', Inactive: 'gray', Monitoring: 'amber' };
const BLANK_COMPETITOR = { name: '', strength: 'Medium', status: 'Active', notes: '' };

const CompetitorsTab = ({ record }) => {
  const toast     = useToast();
  const store     = useStore();
  const wsId      = window.__nx_auth?.user?.activeWorkspaceId;
  const dealId    = record?.id;
  const canManage = window.__nx_rbac?.can('crm.pipeline.manage') ?? false;

  const [competitors, setCompetitors] = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState(null);
  const [formMode,    setFormMode]    = useState(null);  // null | 'add' | 'edit'
  const [editTarget,  setEditTarget]  = useState(null);
  const [draft,       setDraft]       = useState(BLANK_COMPETITOR);
  const [saving,      setSaving]      = useState(false);
  const [removing,    setRemoving]    = useState(null);

  useEffect(() => {
    if (!wsId || !dealId || dealId.includes('_')) { setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    setError(null);
    if (apiAvailable()) {
      window.__nx_api.dealEnrichment.listCompetitors(wsId, dealId)
        .then(r => { if (!cancelled) setCompetitors(Array.isArray(r) ? r : (r?.data ?? [])); })
        .catch(e => { if (!cancelled) setError(e?.message || 'Failed to load competitors'); })
        .finally(() => { if (!cancelled) setLoading(false); });
    } else {
      setLoading(false);
    }
    return () => { cancelled = true; };
  }, [wsId, dealId]);

  const upd = (key, val) => setDraft(d => ({ ...d, [key]: val }));

  const openAdd = () => {
    setDraft(BLANK_COMPETITOR);
    setEditTarget(null);
    setFormMode('add');
  };

  const openEdit = (c) => {
    setDraft({ name: c.name, strength: c.strength, status: c.status, notes: c.notes || '' });
    setEditTarget(c);
    setFormMode('edit');
  };

  const closeForm = () => {
    if (saving) return;
    setFormMode(null);
    setEditTarget(null);
  };

  const handleSave = async () => {
    if (!draft.name.trim()) {
      toast({ tone: 'danger', icon: 'AlertCircle', title: 'Competitor name is required' });
      return;
    }
    setSaving(true);
    try {
      const payload = { name: draft.name.trim(), strength: draft.strength, status: draft.status, notes: draft.notes.trim() };
      if (formMode === 'add') {
        const created = apiAvailable()
          ? await window.__nx_api.dealEnrichment.addCompetitor(wsId, dealId, payload)
          : { id: 'local_' + Date.now(), ...payload };
        setCompetitors(c => [...c, created]);
        toast({ tone: 'success', icon: 'Shield', title: 'Competitor added' });
      } else {
        const updated = apiAvailable()
          ? await window.__nx_api.dealEnrichment.updateCompetitor(wsId, dealId, editTarget.id, payload)
          : { ...editTarget, ...payload };
        setCompetitors(c => c.map(x => x.id === editTarget.id ? updated : x));
        toast({ tone: 'success', icon: 'Check', title: 'Competitor updated' });
      }
      closeForm();
    } catch (e) {
      toast({ tone: 'danger', icon: 'AlertCircle', title: e?.message || (formMode === 'add' ? 'Failed to add competitor' : 'Failed to update competitor') });
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = (c) => {
    store.confirm({
      title:       `Remove ${c.name}?`,
      description: 'This competitor will be permanently removed from this deal.',
      danger:      true,
      onConfirm:   async () => {
        setRemoving(c.id);
        try {
          if (apiAvailable() && !c.id.startsWith('local_')) {
            await window.__nx_api.dealEnrichment.removeCompetitor(wsId, dealId, c.id);
          }
          setCompetitors(prev => prev.filter(x => x.id !== c.id));
          toast({ tone: 'success', icon: 'Trash2', title: 'Competitor removed' });
        } catch (e) {
          toast({ tone: 'danger', icon: 'AlertCircle', title: e?.message || 'Failed to remove competitor' });
        } finally {
          setRemoving(null);
        }
      },
    });
  };

  if (loading) return <div className="nx-drawer-page"><SkeletonRow /><SkeletonRow /></div>;

  /* visible cards: when editing, hide the card being edited (its data is in the form above) */
  const visibleCards = formMode === 'edit'
    ? competitors.filter(c => c.id !== editTarget?.id)
    : competitors;

  return (
    <div className="nx-panel" style={{ padding: '16px 20px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <span style={{ fontWeight: 600, fontSize: 13 }}>Competitors ({competitors.length})</span>
        {canManage && !formMode && (
          <button className="nx-btn nx-btn-sm nx-btn-outline" onClick={openAdd}>
            <NexusIcon name="Plus" size={12} /> Add Competitor
          </button>
        )}
      </div>

      {/* Inline Add / Edit form — same pattern as BuyingCommitteeTab / DealProductsTab */}
      {formMode && (
        <div style={{ background: 'var(--nx-surface-alt)', borderRadius: 8, padding: 12, marginBottom: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <input
            className="nx-input"
            placeholder="Competitor name *"
            value={draft.name}
            onChange={e => upd('name', e.target.value)}
            maxLength={200}
            autoFocus
            style={{ width: '100%' }}
          />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <select className="nx-input" value={draft.strength} onChange={e => upd('strength', e.target.value)}>
              <option value="High">High strength</option>
              <option value="Medium">Medium strength</option>
              <option value="Low">Low strength</option>
              <option value="Unknown">Unknown</option>
            </select>
            <select className="nx-input" value={draft.status} onChange={e => upd('status', e.target.value)}>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
              <option value="Monitoring">Monitoring</option>
            </select>
          </div>
          <textarea
            className="nx-input nx-textarea"
            placeholder="Notes (optional)"
            value={draft.notes}
            onChange={e => upd('notes', e.target.value)}
            maxLength={1000}
            style={{ width: '100%', minHeight: 56 }}
          />
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="nx-btn nx-btn-sm nx-btn-primary" onClick={handleSave} disabled={saving || !draft.name.trim()}>
              {saving ? '…' : formMode === 'add' ? 'Add Competitor' : 'Save Changes'}
            </button>
            <button className="nx-btn nx-btn-sm nx-btn-ghost" onClick={closeForm} disabled={saving}>Cancel</button>
          </div>
        </div>
      )}

      {error && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: 'rgba(220,38,38,0.08)', borderRadius: 6, marginBottom: 12 }}>
          <NexusIcon name="AlertCircle" size={14} style={{ color: 'var(--nx-danger)', flexShrink: 0 }} />
          <span style={{ fontSize: 12, color: 'var(--nx-danger)' }}>{error}</span>
        </div>
      )}

      {!error && competitors.length === 0 && !formMode && (
        <EmptyState
          icon="Shield"
          title="No competitors tracked"
          desc="Track competing vendors to understand your competitive landscape."
          compact
          action={canManage && (
            <button className="nx-btn nx-btn-sm nx-btn-outline" onClick={openAdd}>
              <NexusIcon name="Plus" size={12} /> Add Competitor
            </button>
          )}
        />
      )}

      {visibleCards.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {visibleCards.map(c => (
            <div key={c.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 12px', background: 'var(--nx-surface-alt)', borderRadius: 8, border: '1px solid var(--nx-border)' }}>
              <div style={{ width: 32, height: 32, borderRadius: 8, background: 'var(--nx-bg)', border: '1px solid var(--nx-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <NexusIcon name="Shield" size={14} style={{ color: 'var(--nx-text-muted)' }} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
                  <Pill tone={STRENGTH_TONE[c.strength] || 'gray'}>{c.strength}</Pill>
                  <Pill tone={STATUS_TONE[c.status] || 'gray'}>{c.status}</Pill>
                </div>
                {c.notes && <div style={{ fontSize: 11, color: 'var(--nx-text-muted)', marginTop: 4, lineHeight: 1.4 }}>{c.notes}</div>}
              </div>
              {canManage && (
                <div style={{ display: 'flex', gap: 2, flexShrink: 0 }}>
                  <button className="nx-icon-btn nx-icon-btn-ghost" onClick={() => openEdit(c)}
                    title="Edit" disabled={!!formMode || removing === c.id}>
                    <NexusIcon name="Pencil" size={12} />
                  </button>
                  <button className="nx-icon-btn nx-icon-btn-ghost" onClick={() => handleRemove(c)}
                    title="Remove" disabled={!!formMode || removing === c.id}>
                    <NexusIcon name={removing === c.id ? 'Loader2' : 'Trash2'} size={12} />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
window.CompetitorsTab = CompetitorsTab;

/* ══════════════════════════════════════════════════════════════
   DEAL PRODUCTS TAB
   ══════════════════════════════════════════════════════════════ */
const BLANK_PRODUCT = { name: '', sku: '', quantity: 1, unitPrice: 0, discount: 0 };

const DealProductsTab = ({ record }) => {
  const toast     = useToast();
  const store     = useStore();
  const wsId      = window.__nx_auth?.user?.activeWorkspaceId;
  const dealId    = record?.id;
  const canManage = !!(window.__nx_rbac?.can('crm.pipeline.manage'));

  const [products,   setProducts]   = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState(null);
  const [formMode,   setFormMode]   = useState(null);  // null | 'add' | 'edit'
  const [editTarget, setEditTarget] = useState(null);
  const [draft,      setDraft]      = useState(BLANK_PRODUCT);
  const [saving,     setSaving]     = useState(false);
  const [removing,   setRemoving]   = useState(null);

  useEffect(() => {
    if (!wsId || !dealId || dealId.includes('_')) { setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    setError(null);
    if (apiAvailable()) {
      window.__nx_api.dealEnrichment.listProducts(wsId, dealId)
        .then(r => { if (!cancelled) setProducts(Array.isArray(r) ? r : (r?.data ?? [])); })
        .catch(e => { if (!cancelled) setError(e?.message || 'Failed to load products'); })
        .finally(() => { if (!cancelled) setLoading(false); });
    } else {
      setLoading(false);
    }
    return () => { cancelled = true; };
  }, [wsId, dealId]);

  const lineTotal  = (p) => (p.quantity ?? 1) * (p.unitPrice ?? 0) * (1 - (p.discount ?? 0) / 100);
  const grandTotal = useMemo(() => products.reduce((s, p) => s + lineTotal(p), 0), [products]);

  const draftTotal = useMemo(
    () => (Number(draft.quantity) || 1) * (Number(draft.unitPrice) || 0) * (1 - (Number(draft.discount) || 0) / 100),
    [draft.quantity, draft.unitPrice, draft.discount],
  );

  const openAdd = () => {
    setDraft(BLANK_PRODUCT);
    setEditTarget(null);
    setFormMode('add');
  };

  const openEdit = (p) => {
    setDraft({ name: p.name || '', sku: p.sku || '', quantity: p.quantity ?? 1, unitPrice: p.unitPrice ?? 0, discount: p.discount ?? 0 });
    setEditTarget(p);
    setFormMode('edit');
  };

  const closeForm = () => {
    if (saving) return;
    setFormMode(null);
    setEditTarget(null);
  };

  const handleSave = async () => {
    if (!draft.name.trim() || saving) return;
    setSaving(true);
    const payload = {
      name:      draft.name.trim(),
      sku:       draft.sku.trim() || undefined,
      quantity:  Number(draft.quantity) || 1,
      unitPrice: Number(draft.unitPrice) || 0,
      discount:  Number(draft.discount)  || 0,
    };
    try {
      if (formMode === 'add') {
        const created = apiAvailable()
          ? await window.__nx_api.dealEnrichment.addProduct(wsId, dealId, payload)
          : { id: 'local_' + Date.now(), ...payload, total: draftTotal };
        setProducts(p => [...p, created]);
        toast({ tone: 'success', icon: 'Package', title: 'Product added' });
      } else {
        const updated = apiAvailable()
          ? await window.__nx_api.dealEnrichment.updateProduct(wsId, dealId, editTarget.id, payload)
          : { ...editTarget, ...payload, total: draftTotal };
        setProducts(p => p.map(x => x.id === editTarget.id ? updated : x));
        toast({ tone: 'success', icon: 'Check', title: 'Product updated' });
      }
      closeForm();
    } catch (e) {
      toast({ tone: 'danger', icon: 'AlertCircle', title: e?.message || 'Failed to save product' });
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = (p) => {
    store.confirm({
      title:       `Remove "${p.name}"?`,
      description: 'This line item will be permanently deleted from the deal.',
      danger:      true,
      onConfirm:   async () => {
        setRemoving(p.id);
        try {
          if (apiAvailable() && !p.id.startsWith('local_')) {
            await window.__nx_api.dealEnrichment.removeProduct(wsId, dealId, p.id);
          }
          setProducts(all => all.filter(x => x.id !== p.id));
        } catch (e) {
          toast({ tone: 'danger', icon: 'AlertCircle', title: e?.message || 'Failed to remove product' });
        } finally {
          setRemoving(null);
        }
      },
    });
  };

  const visibleRows = formMode === 'edit'
    ? products.filter(p => p.id !== editTarget?.id)
    : products;

  if (loading) return <div className="nx-drawer-page"><SkeletonRow /><SkeletonRow /></div>;

  return (
    <div className="nx-panel" style={{ padding: '16px 20px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <span style={{ fontWeight: 600, fontSize: 13 }}>
          Products / Line Items{products.length > 0 && <span style={{ fontWeight: 400, color: 'var(--nx-text-muted)', marginLeft: 4 }}>({products.length})</span>}
        </span>
        {canManage && !formMode && (
          <button type="button" className="nx-btn nx-btn-sm nx-btn-outline" onClick={openAdd}>
            <NexusIcon name="Plus" size={12} /> Add Product
          </button>
        )}
      </div>

      {/* Inline add / edit form */}
      {formMode && (
        <div style={{ background: 'var(--nx-surface-alt)', borderRadius: 8, padding: 12, marginBottom: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 8 }}>
            <input
              className="nx-input"
              placeholder="Product name *"
              value={draft.name}
              onChange={e => setDraft(d => ({ ...d, name: e.target.value }))}
              autoFocus
              disabled={saving}
              style={{ width: '100%' }}
            />
            <input
              className="nx-input"
              placeholder="SKU"
              value={draft.sku}
              onChange={e => setDraft(d => ({ ...d, sku: e.target.value }))}
              disabled={saving}
              style={{ width: '100%' }}
            />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 11, color: 'var(--nx-text-muted)' }}>
              Qty
              <input className="nx-input" type="number" min={1} value={draft.quantity} onChange={e => setDraft(d => ({ ...d, quantity: e.target.value }))} disabled={saving} />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 11, color: 'var(--nx-text-muted)' }}>
              Unit Price ($)
              <input className="nx-input" type="number" min={0} step="0.01" value={draft.unitPrice} onChange={e => setDraft(d => ({ ...d, unitPrice: e.target.value }))} disabled={saving} />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 11, color: 'var(--nx-text-muted)' }}>
              Discount (%)
              <input className="nx-input" type="number" min={0} max={100} value={draft.discount} onChange={e => setDraft(d => ({ ...d, discount: e.target.value }))} disabled={saving} />
            </label>
          </div>
          {(Number(draft.unitPrice) > 0) && (
            <div style={{ fontSize: 11, color: 'var(--nx-text-muted)' }}>
              Line total: <span style={{ fontWeight: 600, color: 'var(--nx-accent)' }}>{fmtMoney(draftTotal)}</span>
            </div>
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" className="nx-btn nx-btn-sm nx-btn-primary" onClick={handleSave} disabled={!draft.name.trim() || saving}>
              {saving ? '…' : formMode === 'add' ? 'Add Product' : 'Save Changes'}
            </button>
            <button type="button" className="nx-btn nx-btn-sm nx-btn-ghost" onClick={closeForm} disabled={saving}>Cancel</button>
          </div>
        </div>
      )}

      {error && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: 'rgba(220,38,38,0.08)', borderRadius: 6, marginBottom: 12 }}>
          <NexusIcon name="AlertCircle" size={14} style={{ color: 'var(--nx-danger)', flexShrink: 0 }} />
          <span style={{ fontSize: 12, color: 'var(--nx-danger)' }}>{error}</span>
        </div>
      )}

      {!error && products.length === 0 && !formMode && (
        <EmptyState
          icon="Package"
          title="No products added"
          desc={canManage ? 'Add products or line items to this deal.' : 'No products have been added to this deal yet.'}
          compact
          action={canManage && (
            <button type="button" className="nx-btn nx-btn-sm nx-btn-outline" onClick={openAdd}>
              <NexusIcon name="Plus" size={12} /> Add Product
            </button>
          )}
        />
      )}

      {visibleRows.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--nx-border)' }}>
                {['Name', 'SKU', 'Qty', 'Unit Price', 'Disc.', 'Total', ''].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--nx-text-muted)', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visibleRows.map(p => (
                <tr key={p.id} style={{ borderBottom: '1px solid var(--nx-border)', opacity: removing === p.id ? 0.5 : 1 }}>
                  <td style={{ padding: '8px', fontWeight: 500 }}>{p.name}</td>
                  <td style={{ padding: '8px', color: 'var(--nx-text-muted)' }}>{p.sku || '—'}</td>
                  <td style={{ padding: '8px' }}>{p.quantity}</td>
                  <td style={{ padding: '8px' }}>{fmtMoney(p.unitPrice)}</td>
                  <td style={{ padding: '8px' }}>{p.discount ? p.discount + '%' : '—'}</td>
                  <td style={{ padding: '8px', fontWeight: 600, color: 'var(--nx-accent)' }}>{fmtMoney(lineTotal(p))}</td>
                  <td style={{ padding: '8px' }}>
                    {canManage && (
                      <div style={{ display: 'flex', gap: 2 }}>
                        <button
                          type="button"
                          className="nx-icon-btn nx-icon-btn-ghost"
                          onClick={() => openEdit(p)}
                          disabled={!!formMode || removing === p.id}
                          title="Edit"
                        >
                          <NexusIcon name="Pencil" size={11} />
                        </button>
                        <button
                          type="button"
                          className="nx-icon-btn nx-icon-btn-ghost"
                          onClick={() => handleRemove(p)}
                          disabled={!!formMode || removing === p.id}
                          title="Remove"
                        >
                          <NexusIcon name={removing === p.id ? 'Loader2' : 'Trash2'} size={11} />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
              <tr style={{ background: 'var(--nx-surface-alt)' }}>
                <td colSpan={5} style={{ padding: '8px', fontWeight: 700, textAlign: 'right', fontSize: 12 }}>Grand Total</td>
                <td style={{ padding: '8px', fontWeight: 800, color: 'var(--nx-accent)', fontSize: 13 }}>{fmtMoney(grandTotal)}</td>
                <td />
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
window.DealProductsTab = DealProductsTab;

/* ══════════════════════════════════════════════════════════════
   DEAL CHECKLIST TAB
   ══════════════════════════════════════════════════════════════ */
const DealChecklistTab = ({ record }) => {
  const toast     = useToast();
  const store     = useStore();
  const wsId      = window.__nx_auth?.user?.activeWorkspaceId;
  const dealId    = record?.id;
  const canManage = window.__nx_rbac?.can('crm.pipeline.manage') ?? false;

  const [items,    setItems]    = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [saving,   setSaving]   = useState(false);

  useEffect(() => {
    if (!wsId || !dealId || dealId.includes('_')) { setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    setError(null);
    if (apiAvailable()) {
      window.__nx_api.dealEnrichment.listChecklist(wsId, dealId)
        .then(r => { if (!cancelled) setItems(Array.isArray(r) ? r : (r?.data ?? [])); })
        .catch(e => { if (!cancelled) setError(e?.message || 'Failed to load checklist'); })
        .finally(() => { if (!cancelled) setLoading(false); });
    } else {
      setLoading(false);
    }
    return () => { cancelled = true; };
  }, [wsId, dealId]);

  const done  = useMemo(() => items.filter(i => i.done).length, [items]);
  const total = items.length;
  const pct   = total ? Math.round((done / total) * 100) : 0;

  const openForm  = () => { setNewLabel(''); setShowForm(true); };
  const closeForm = () => {
    if (saving) return;
    setShowForm(false);
    setNewLabel('');
  };

  const handleToggle = async (item) => {
    if (!canManage) return;
    const optimistic = { ...item, done: !item.done };
    setItems(all => all.map(i => i.id === item.id ? optimistic : i));
    try {
      if (apiAvailable() && !item.id.startsWith('local_')) {
        const updated = await window.__nx_api.dealEnrichment.toggleChecklist(wsId, dealId, item.id);
        if (updated) setItems(all => all.map(i => i.id === item.id ? updated : i));
      }
    } catch {
      setItems(all => all.map(i => i.id === item.id ? item : i));
      toast({ tone: 'danger', icon: 'AlertCircle', title: 'Failed to update item' });
    }
  };

  const handleAdd = async () => {
    if (!newLabel.trim() || saving) return;
    setSaving(true);
    try {
      const created = apiAvailable()
        ? await window.__nx_api.dealEnrichment.addChecklistItem(wsId, dealId, { title: newLabel.trim() })
        : { id: 'local_' + Date.now(), title: newLabel.trim(), done: false };
      setItems(i => [...i, created]);
      closeForm();
    } catch (e) {
      toast({ tone: 'danger', icon: 'AlertCircle', title: e?.message || 'Failed to add item' });
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = (id, title) => {
    store.confirm({
      title:       `Remove "${title}"?`,
      description: 'This checklist item will be permanently deleted.',
      danger:      true,
      onConfirm:   async () => {
        try {
          if (apiAvailable() && !id.startsWith('local_')) {
            await window.__nx_api.dealEnrichment.removeChecklist(wsId, dealId, id);
          }
          setItems(i => i.filter(x => x.id !== id));
        } catch (e) {
          toast({ tone: 'danger', icon: 'AlertCircle', title: e?.message || 'Failed to remove item' });
        }
      },
    });
  };

  if (loading) return <div className="nx-drawer-page"><SkeletonRow /><SkeletonRow /></div>;

  return (
    <div className="nx-panel" style={{ padding: '16px 20px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <span style={{ fontWeight: 600, fontSize: 13 }}>Deal Checklist</span>
        {canManage && !showForm && (
          <button type="button" className="nx-btn nx-btn-sm nx-btn-outline" onClick={openForm}>
            <NexusIcon name="Plus" size={12} /> Add Item
          </button>
        )}
      </div>

      {/* Inline add form — same pattern as CompetitorsTab */}
      {showForm && (
        <div style={{ background: 'var(--nx-surface-alt)', borderRadius: 8, padding: 12, marginBottom: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <input
            className="nx-input"
            placeholder="Checklist item…"
            value={newLabel}
            onChange={e => setNewLabel(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') { handleAdd(); }
              else if (e.key === 'Escape') { closeForm(); }
            }}
            autoFocus
            disabled={saving}
            style={{ width: '100%' }}
          />
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" className="nx-btn nx-btn-sm nx-btn-primary" onClick={handleAdd} disabled={!newLabel.trim() || saving}>
              {saving ? '…' : 'Add Item'}
            </button>
            <button type="button" className="nx-btn nx-btn-sm nx-btn-ghost" onClick={closeForm} disabled={saving}>Cancel</button>
          </div>
        </div>
      )}

      {error && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: 'rgba(220,38,38,0.08)', borderRadius: 6, marginBottom: 12 }}>
          <NexusIcon name="AlertCircle" size={14} style={{ color: 'var(--nx-danger)', flexShrink: 0 }} />
          <span style={{ fontSize: 12, color: 'var(--nx-danger)' }}>{error}</span>
        </div>
      )}

      {total > 0 && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ height: 5, background: 'var(--nx-border)', borderRadius: 3, overflow: 'hidden' }}>
            <div style={{ width: pct + '%', height: '100%', background: pct === 100 ? 'var(--nx-success)' : 'var(--nx-accent)', borderRadius: 3, transition: 'width 0.3s ease' }} />
          </div>
          <span style={{ fontSize: 10, color: 'var(--nx-text-muted)', marginTop: 3, display: 'block' }}>{done}/{total} · {pct}% complete</span>
        </div>
      )}

      {!error && items.length === 0 && !showForm && (
        <EmptyState
          icon="CheckSquare"
          title="No checklist items"
          desc={canManage ? 'Add steps to track deal progress.' : 'No steps have been added to this deal yet.'}
          compact
          action={canManage && (
            <button type="button" className="nx-btn nx-btn-sm nx-btn-outline" onClick={openForm}>
              <NexusIcon name="Plus" size={12} /> Add Item
            </button>
          )}
        />
      )}

      {items.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {items.map((item, idx) => (
            <div key={item.id} style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0',
              borderBottom: idx < items.length - 1 ? '1px solid var(--nx-border)' : 'none',
            }}>
              <button
                type="button"
                onClick={() => handleToggle(item)}
                disabled={!canManage}
                style={{
                  width: 16, height: 16, borderRadius: 3, border: '1.5px solid',
                  borderColor: item.done ? 'var(--nx-success)' : 'var(--nx-border-strong, var(--nx-border))',
                  background: item.done ? 'var(--nx-success)' : 'transparent',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: canManage ? 'pointer' : 'default', flexShrink: 0, padding: 0,
                  transition: 'background 0.15s, border-color 0.15s',
                }}
              >
                {item.done && <NexusIcon name="Check" size={9} style={{ color: '#fff' }} />}
              </button>
              <span style={{
                flex: 1, fontSize: 13,
                textDecoration: item.done ? 'line-through' : 'none',
                color: item.done ? 'var(--nx-text-muted)' : 'var(--nx-text)',
              }}>
                {item.title ?? item.label}
              </span>
              {canManage && (
                <button
                  type="button"
                  className="nx-icon-btn nx-icon-btn-ghost"
                  onClick={() => handleRemove(item.id, item.title ?? item.label)}
                  title="Remove item"
                >
                  <NexusIcon name="Trash2" size={11} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
window.DealChecklistTab = DealChecklistTab;

/* ══════════════════════════════════════════════════════════════
   DEAL INTELLIGENCE TAB
   ══════════════════════════════════════════════════════════════ */
const DealIntelligenceTab = ({ record }) => {
  const store  = useStore();
  const wsId   = window.__nx_auth?.user?.activeWorkspaceId;
  const toast  = useToast();

  const [nextAction, setNextAction] = useState(record?.nextAction || '');
  const [editing,    setEditing]    = useState(false);

  const stage    = (store.pipeline || []).find(s => s.id === record?.stageId);
  const isWon    = stage?.kind === 'WON';
  const isLost   = stage?.kind === 'LOST';

  /* Compute deal score factors */
  const factors = useMemo(() => {
    const today      = new Date();
    const closeDate  = record?.closeDate ? new Date(record.closeDate) : null;
    const daysToClose = closeDate ? Math.ceil((closeDate - today) / 86_400_000) : null;
    const daysInStage = record?.stageEnteredAt ? daysBetween(record.stageEnteredAt, today) : (record?.age || 0);
    const prob        = stage ? (stage.prob ?? null) : null;
    const activities  = (store.activity || []).filter(a => a.dealId === record?.id);
    const lastActivity = activities.length ? activities[0] : null;
    const activityScore = Math.min(100, activities.length * 10);

    const closeScore  = daysToClose == null ? 50 : daysToClose < 0 ? 0 : daysToClose <= 7 ? 90 : daysToClose <= 30 ? 70 : daysToClose <= 90 ? 50 : 30;
    const probScore   = prob ?? 0;
    const stageScore  = daysInStage <= 7 ? 90 : daysInStage <= 14 ? 70 : daysInStage <= 30 ? 40 : 10;
    const dealScore   = Math.round((closeScore * 0.2 + probScore * 0.35 + stageScore * 0.25 + activityScore * 0.2));

    return {
      dealScore: Math.min(100, Math.max(0, dealScore)),
      closeDate: closeDate ? closeDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : null,
      daysToClose,
      daysInStage,
      prob,
      activityCount: activities.length,
      lastActivityTime: lastActivity?.time || null,
      closeScore,
      stageScore,
      activityScore,
    };
  }, [record, stage, store.activity]);

  const { dealScore } = factors;
  const scoreColor = dealScore >= 70 ? 'var(--nx-success)' : dealScore >= 40 ? '#F59E0B' : 'var(--nx-danger)';
  const scoreLabel = dealScore >= 70 ? 'Healthy' : dealScore >= 40 ? 'At Risk' : 'Critical';

  const circumference = 2 * Math.PI * 36;
  const dashOffset    = circumference - (dealScore / 100) * circumference;

  const slaHours     = stage?.slaHours;
  const slaStatus    = slaHours ? (factors.daysInStage * 24 > slaHours ? 'Breached' : factors.daysInStage * 24 > slaHours * 0.8 ? 'At Risk' : 'On Track') : 'Not configured';
  const slaTone      = slaStatus === 'Breached' ? 'red' : slaStatus === 'At Risk' ? 'amber' : slaStatus === 'On Track' ? 'green' : 'gray';

  const handleSaveAction = async () => {
    store.update('deals', record.id, { nextAction });
    toast({ tone: 'success', icon: 'Check', title: 'Next action saved' });
    setEditing(false);
  };

  return (
    <div className="nx-panel" style={{ padding: '16px 20px' }}>

      {/* Deal Score Ring */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 20, padding: '12px 0', marginBottom: 16, borderBottom: '1px solid var(--nx-divider)' }}>
        <svg width={88} height={88} viewBox="0 0 88 88" style={{ flexShrink: 0 }}>
          <circle cx={44} cy={44} r={36} fill="none" stroke="var(--nx-divider)" strokeWidth={8} />
          <circle cx={44} cy={44} r={36} fill="none" stroke={scoreColor} strokeWidth={8}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={dashOffset}
            transform="rotate(-90 44 44)"
            style={{ transition: 'stroke-dashoffset 0.5s ease' }}
          />
          <text x={44} y={40} textAnchor="middle" fontSize={20} fontWeight={700} fill={scoreColor}>{dealScore}</text>
          <text x={44} y={54} textAnchor="middle" fontSize={9} fill="var(--nx-text-muted)">{scoreLabel}</text>
        </svg>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>Deal Health Score</div>
          <div style={{ fontSize: 12, color: 'var(--nx-text-muted)', lineHeight: 1.5 }}>
            {dealScore >= 70 ? 'This deal is on track. Strong engagement and close date alignment.' :
             dealScore >= 40 ? 'Some risk factors present. Review close date and stage progression.' :
             'Deal needs immediate attention. Stalling signals detected.'}
          </div>
          <div style={{ marginTop: 6 }}>
            <Pill tone={slaTone}>SLA: {slaStatus}</Pill>
          </div>
        </div>
      </div>

      {/* Health Indicators */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontWeight: 600, fontSize: 12, color: 'var(--nx-text-muted)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Health Indicators</div>
        {[
          { label: 'Close Date Proximity', score: factors.closeScore, detail: factors.daysToClose == null ? 'No close date set' : factors.daysToClose < 0 ? 'Overdue by ' + Math.abs(factors.daysToClose) + 'd' : factors.daysToClose + ' days remaining' },
          { label: 'Win Probability',      score: factors.prob ?? 0,   detail: factors.prob != null ? factors.prob + '%' : 'Unknown' },
          { label: 'Days in Stage',        score: factors.stageScore,  detail: factors.daysInStage + 'd in current stage' },
          { label: 'Activity Recency',     score: factors.activityScore, detail: factors.lastActivityTime ? 'Last: ' + factors.lastActivityTime : 'No activity recorded' },
        ].map(f => {
          const fc = f.score >= 70 ? 'var(--nx-success)' : f.score >= 40 ? '#F59E0B' : 'var(--nx-danger)';
          return (
            <div key={f.label} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
              <div style={{ width: 130, fontSize: 12, color: 'var(--nx-text)', flexShrink: 0, lineHeight: 1.3 }}>{f.label}</div>
              <div style={{ flex: '1 1 0', minWidth: 60, height: 6, background: 'var(--nx-divider)', borderRadius: 3, overflow: 'hidden' }}>
                <div style={{ width: Math.min(100, f.score) + '%', height: '100%', background: fc, borderRadius: 3 }} />
              </div>
              <div style={{ width: 120, flexShrink: 0, fontSize: 12, color: 'var(--nx-text-muted)', textAlign: 'right', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.detail}</div>
            </div>
          );
        })}
      </div>

      {/* Next Action */}
      <div style={{ background: 'var(--nx-surface-alt)', borderRadius: 'var(--nx-radius)', padding: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
          <span style={{ fontWeight: 600, fontSize: 12 }}>Next Action</span>
          {!editing && (
            <button className="nx-btn nx-btn-xs nx-btn-ghost" onClick={() => setEditing(true)}>
              <NexusIcon name="Edit2" size={10} /> Edit
            </button>
          )}
        </div>
        {editing ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <input
              className="nx-input"
              placeholder="What's the next step to advance this deal?"
              value={nextAction}
              onChange={e => setNextAction(e.target.value)}
              autoFocus
            />
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="nx-btn nx-btn-sm nx-btn-primary" onClick={handleSaveAction}>Save</button>
              <button className="nx-btn nx-btn-sm nx-btn-ghost" onClick={() => { setEditing(false); setNextAction(record?.nextAction || ''); }}>Cancel</button>
            </div>
          </div>
        ) : (
          <p style={{ fontSize: 12, color: nextAction ? 'var(--nx-text)' : 'var(--nx-text-faint)', margin: 0, lineHeight: 1.5 }}>
            {nextAction || 'No next action defined. Click Edit to add one.'}
          </p>
        )}
      </div>
    </div>
  );
};
window.DealIntelligenceTab = DealIntelligenceTab;

/* ══════════════════════════════════════════════════════════════
   DEAL REOPEN MODAL
   ══════════════════════════════════════════════════════════════ */
const DealReopenModal = ({ record, onClose, onReopened }) => {
  const store   = useStore();
  const toast   = useToast();
  const wsId    = window.__nx_auth?.user?.activeWorkspaceId;

  const openStages = useMemo(() =>
    (store.pipeline || []).filter(s => !/\b(won|lost)\b/i.test(s.name)),
    [store.pipeline]
  );
  const [stageId,  setStageId]  = useState(openStages[0]?.id || '');
  const [loading,  setLoading]  = useState(false);

  const handleReopen = async () => {
    if (!stageId) return;
    setLoading(true);
    try {
      if (apiAvailable() && !record.id.startsWith('local_')) {
        await window.__nx_api.dealEnrichment.reopenDeal(wsId, record.id, stageId);
      }
      store.update('deals', record.id, { stageId });
      toast({ tone: 'success', icon: 'RotateCcw', title: record.title + ' reopened' });
      onReopened?.();
      onClose?.();
    } catch (e) {
      toast({ tone: 'danger', icon: 'AlertCircle', title: e.message || 'Failed to reopen deal' });
    }
    setLoading(false);
  };

  return (
    <Modal
      title="Reopen Deal"
      icon="RotateCcw"
      onClose={onClose}
      width={360}
      footer={
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="nx-btn nx-btn-ghost" onClick={onClose} disabled={loading}>Cancel</button>
          <button className="nx-btn nx-btn-primary" onClick={handleReopen} disabled={loading || !stageId}>
            {loading ? 'Reopening...' : 'Reopen Deal'}
          </button>
        </div>
      }
    >
      <p style={{ fontSize: 13, color: 'var(--nx-text-muted)', marginBottom: 16, lineHeight: 1.5 }}>
        Select the stage to move <strong>{record?.title}</strong> back to.
      </p>
      <select className="nx-select" style={{ width: '100%' }} value={stageId} onChange={e => setStageId(e.target.value)}>
        {openStages.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
      </select>
    </Modal>
  );
};
window.DealReopenModal = DealReopenModal;
